// ─── generators/zendingen.js ──────────────────────────────────────────────────
// Rapportgenerator: genZendingen.
// Wijzig hier bij aanpassingen aan dit specifieke rapporttype.

import { MAANDEN, MAANDEN_K, fmtE, fmtD, fmtKg, fmtK, diff, statusLabel, statusBadge, isOnderweg } from '../helpers.js';
import { header, kpiBlok, pageCss, sectTitel, sectTitelKlein, footer, wrapMetHerhalendeHeader } from '../layout.js';
import { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek } from '../charts.js';
import { verwerkData } from '../data.js';

function genZendingen(aanvragen, bedrijfsnaam, cfg, logo) {
  const liggend = (cfg.orientatie||'liggend') === 'liggend';
  let lijst = aanvragen.map(a=>({...a,_sl:statusLabel(a)}));
  if(cfg.van) lijst=lijst.filter(a=>(a.Datum||a.LaadDatum||'')>=cfg.van);
  if(cfg.tot) lijst=lijst.filter(a=>(a.Datum||a.LaadDatum||'')<=cfg.tot);
  if(cfg.statussen&&!cfg.statussen.includes('alle')) lijst=lijst.filter(a=>cfg.statussen.includes(a._sl));
  lijst.sort((a,b)=>{
    if(cfg.sorteer==='datum-asc') return new Date(a.Datum||0)-new Date(b.Datum||0);
    if(cfg.sorteer==='status') return a._sl.localeCompare(b._sl);
    if(cfg.sorteer==='kosten-desc') return (parseFloat(b.Factuur||b.KlantPrijs)||0)-(parseFloat(a.Factuur||a.KlantPrijs)||0);
    return new Date(b.Datum||0)-new Date(a.Datum||0);
  });

  const totK   = lijst.reduce((s,a)=>s+(parseFloat(a.Factuur)||parseFloat(a.KlantPrijs)||0),0);
  const totGew = lijst.reduce((s,a)=>{let g=0;[1,2,3,4,5].forEach(n=>g+=parseFloat(a[`Goed${n}_Gewicht`])||0);return s+g;},0);
  const periode = cfg.van||cfg.tot ? `${fmtD(cfg.van||'')} — ${fmtD(cfg.tot||'')}` : 'Alle periodes';

  const _css = '<style>' + pageCss(cfg.orientatie) + '</style>';
  const _hdr = header('Zendingenoverzicht', `${lijst.length} zendingen · ${periode}`, bedrijfsnaam, logo);
  let body = '';

  // Samenvatting
  body += kpiBlok([
    {lbl:'Zendingen', val:lijst.length, kleur:'#1a364c'},
    {lbl:'Totaal gewicht', val:totGew>0?fmtKg(totGew):'—', kleur:'#8b5cf6'},
    {lbl:'Totale kosten', val:fmtE(totK), kleur:'#d4a848'},
    {lbl:'Gemiddeld', val:lijst.length>0?fmtE(totK/lijst.length):'—', kleur:'#10b981'},
  ]);

  // Kolom headers
  const cols = ['Nr.','Datum','Laadplaats','Losplaats','Laaddatum','Losdatum','Goederen'];
  if(cfg.inclGewicht!==false) cols.push('Gewicht');
  if(cfg.inclKosten!==false) cols.push('Prijs excl.');

  // Status samenvatting boven tabel
  const statusGroepen = {};
  lijst.forEach(a=>{ statusGroepen[a._sl]=(statusGroepen[a._sl]||0)+1; });
  const statusSamenv = Object.entries(statusGroepen)
    .map(([s,n])=>`<span style="display:inline-flex;align-items:center;gap:4px;margin-right:10px;font-size:10px;">${statusBadge(s)}<span style="color:#64748b;font-size:10px;margin-left:2px;">${n}×</span></span>`)
    .join('');
  body += `<div style="margin-bottom:10px;display:flex;align-items:center;gap:4px;flex-wrap:wrap;">
    <span style="font-size:9px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.05em;margin-right:6px;">Status:</span>
    ${statusSamenv}
  </div>`;

  const cellPad = liggend ? '4px 6px' : '4px 5px';
  const thPad   = liggend ? '5px 6px' : '5px 5px';
  const fs      = liggend ? '9px' : '8.5px';
  const thFs    = liggend ? '8px' : '7.5px';
  // Staand: kleinere kolommen zodat alles past zonder overlap
  const colWidths = liggend ? {
    'Nr.':'72px','Datum':'56px','Laadplaats':'92px','Losplaats':'92px',
    'Laaddatum':'66px','Losdatum':'66px','Goederen':'auto',
    'Gewicht':'56px','Prijs excl.':'68px'
  } : {
    'Nr.':'60px','Datum':'46px','Laadplaats':'68px','Losplaats':'68px',
    'Laaddatum':'52px','Losdatum':'52px','Goederen':'auto',
    'Gewicht':'46px','Prijs excl.':'58px'
  };
  body += `<table style="width:100%;border-collapse:collapse;font-size:${fs};table-layout:fixed;">
    <colgroup>
      ${cols.map(c=>`<col style="width:${colWidths[c]||'auto'}"/>`).join('')}
    </colgroup>
    <thead><tr style="background:#f7f6f2;">
      ${cols.map(c=>`<th style="padding:${thPad};text-align:${c==='Prijs excl.'?'right':'left'};font-size:${thFs};font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.04em;border-bottom:1px solid #e8edf3;white-space:nowrap;">${c}</th>`).join('')}
    </tr></thead>
    <tbody>
      ${lijst.map((a,i)=>{
        let gew=0;[1,2,3,4,5].forEach(n=>gew+=parseFloat(a[`Goed${n}_Gewicht`])||0);
        return `<tr style="border-bottom:1px solid #f5f5f5;background:${i%2===0?'white':'#fafafa'};">
          <td style="padding:${cellPad};font-weight:700;color:#1a364c;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${a.AanvraagID}</td>
          <td style="padding:${cellPad};white-space:nowrap;">${fmtD(a.Datum)}</td>
          <td style="padding:${cellPad};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${a.LaadPlaats||a.LaadLand||'—'}</td>
          <td style="padding:${cellPad};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${a.LosPlaats||a.LosLand||'—'}</td>
          <td style="padding:${cellPad};white-space:nowrap;">${fmtD(a.LaadDatum)}</td>
          <td style="padding:${cellPad};white-space:nowrap;">${fmtD(a.LosDatum)}</td>
          <td style="padding:${cellPad};overflow:hidden;text-overflow:ellipsis;">${a.Goed1_Omschrijving||a.Goed1_Soort||'—'}</td>
          ${cfg.inclGewicht!==false?`<td style="padding:${cellPad};white-space:nowrap;text-align:right;font-variant-numeric:tabular-nums;">${gew>0?fmtKg(gew):'—'}</td>`:''}
          ${cfg.inclKosten!==false?`<td style="padding:${cellPad};text-align:right;font-weight:600;white-space:nowrap;font-variant-numeric:tabular-nums;">${fmtE(parseFloat(a.Factuur)||parseFloat(a.KlantPrijs))}</td>`:''}
        </tr>`;
      }).join('')}
    </tbody>
    <tfoot><tr style="border-top:2px solid #1a364c;background:#f7f6f2;">
      <td colspan="7" style="padding:6px 5px;font-weight:700;color:#1a364c;font-size:${fs};">Totaal: ${lijst.length} zendingen</td>
      ${cfg.inclGewicht!==false?`<td style="padding:6px 5px;font-weight:700;text-align:right;font-size:${fs};font-variant-numeric:tabular-nums;">${totGew>0?fmtKg(totGew):'—'}</td>`:''}
      ${cfg.inclKosten!==false?`<td style="padding:6px 5px;text-align:right;font-weight:700;color:#1a364c;font-size:${fs};font-variant-numeric:tabular-nums;">${fmtE(totK)}</td>`:''}
    </tr></tfoot>
  </table>`;

  return wrapMetHerhalendeHeader(_css, _hdr, body);
}

export { genZendingen };
